import { useCallback, useState } from 'react'

import { isSNSDomain, resolveSNSDomain } from '@src/chains/solana/utils/sns'
import { logger } from '@src/utils/logger'

interface UseSNSResolverResult {
    resolvedAddress: string | null
    isResolving: boolean
    error: Error | null
    resolveAddress: (address: string) => Promise<string | null>
}

/**
 * Hook to resolve SNS domains to Solana addresses
 * @returns Object with resolvedAddress, isResolving, error, and resolveAddress function
 */
export const useSNSResolver = (): UseSNSResolverResult => {
    const [resolvedAddress, setResolvedAddress] = useState<string | null>(null)
    const [isResolving, setIsResolving] = useState<boolean>(false)
    const [error, setError] = useState<Error | null>(null)

    /**
     * Resolves an address that might be an SNS domain
     * @param address The address or SNS domain to resolve
     * @returns The resolved Solana address or the original address if not an SNS domain
     */
    const resolveAddress = useCallback(async (address: string): Promise<string | null> => {
        // Reset state
        setError(null)

        // If not an SNS domain, return the address as is
        if (!isSNSDomain(address)) {
            return address
        }

        try {
            setIsResolving(true)
            logger.debug(`Resolving SNS domain: ${address}`)

            const resolved = await resolveSNSDomain(address)

            if (!resolved) {
                throw new Error(`Could not resolve SNS domain: ${address}`)
            }

            logger.debug(`Resolved ${address} to ${resolved}`)
            setResolvedAddress(resolved)
            return resolved
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err))
            setError(error)
            logger.error('SNS resolution error:', error)
            return null
        } finally {
            setIsResolving(false)
        }
    }, [])

    return {
        resolvedAddress,
        isResolving,
        error,
        resolveAddress,
    }
}

export default useSNSResolver
